<template>
  <div class="list-container">
    <div class="top-fix-area" ref="topFixArea">
      <div class="d-flex justify-content-between align-items-center">
        <h1 class="page-title">請求情報一覧</h1>
        <div>
          <router-link :to="{ name: 'NonBillingList' }"> <!-- 未請求作業一覧へのリンク -->
            <button type="button" class="btn btn-primary mx-1">未請求作業一覧へ</button>
          </router-link>
          <router-link :to="{ name: 'Home' }"> <!-- ホームへのリンク -->
            <button type="button" class="btn btn-primary mx-1">ホーム</button>
          </router-link>
        </div>
      </div>

      <!-- 検索バー拡張: 年月範囲検索 + 請求番号 + 請求先顧客 -->
      <SearchPanelWrapper :key="route.fullPath" v-model="isSearchExpanded" :getTableElement="() => tableWrapper?.$el || tableWrapper?.value" >
        <form class="search-container" style="background-color: #f8f9fa;" @submit.prevent="searchBuildings"> <!-- 背景色を薄く設定 -->
          <div class="row g-3 align-items-center">
            <div class="d-flex flex-wrap align-items-center mb-2">
              <!-- 年月From -->
              <div class="d-flex align-items-center my-2 me-3">
                <label class="col-form-label me-2">請求年月</label>

                <!-- From -->
                <div class="d-flex align-items-center me-2">
                  <select class="form-select me-1" v-model="searchYearFrom" style="min-width: 120px">
                    <option value="" selected>年</option>
                    <option v-for="year in years" :key="year" :value="year">{{ year }}年</option>
                  </select>
                  <select class="form-select" v-model="searchMonthFrom" style="min-width: 100px">
                    <option value="" selected>月</option>
                    <option v-for="month in months" :key="month" :value="month">{{ month }}月</option>
                  </select>
                </div>

                <label class="col-form-label me-2">〜</label>

                <!-- To -->
                <div class="d-flex align-items-center">
                  <select class="form-select me-1" v-model="searchYearTo" style="min-width: 120px">
                    <option value="" selected>年</option>
                    <option v-for="year in filteredYearsTo" :key="'to-' + year" :value="year">{{ year }}年</option>
                  </select>
                  <select class="form-select" v-model="searchMonthTo" style="min-width: 100px">
                    <option value="" selected>月</option>
                    <option v-for="month in filteredMonthsTo" :key="'to-month-' + month" :value="month">{{ month }}月</option>
                  </select>
                </div>
              </div>

              <!-- 請求番号検索 -->
              <div class="d-flex align-items-center my-2 me-3">
                <input type="text" class="form-control" v-model="searchBillingNo" placeholder="請求番号" />
              </div>

              <!-- 請求先顧客検索 -->
              <div class="d-flex align-items-center my-2 me-3">
                <div class="col-auto">
                  <CustomerSelectBox
                    v-model="searchCustomerName"
                    :options="customers"
                    placeholder="請求先顧客名で検索"
                    :formatter="c => c.customer_nm"
                  />
                </div>
              </div>

              <!-- PDF出力フラグ -->
              <div class="d-flex align-items-center my-2 me-3">
                <select v-model="searchPdfOutputFlag" class="form-select" style="min-width: 150px">
                  <option value="">すべて</option>
                  <option value="true">PDF出力済み</option>
                  <option value="false">PDF未出力</option>
                </select>
              </div>


              <!-- 検索ボタン -->
              <div class="ms-2">
                <button type="submit" class="btn btn-sm btn-primary mx-1">検索</button>
                <button type="button" class="btn btn-sm btn-secondary mx-1" @click="searchResetBuildings">クリア</button>
              </div>
            </div>
          </div>
        </form>
      </SearchPanelWrapper>
    </div>

    <div class="d-flex justify-content-end" ref="paginationContainer">
      <Pagination
        :currentPage="currentPage"
        :totalPages="totalPages"
        @change-page="handleChangePage"
      />
    </div>

    <div class="table-wrapper" ref="tableWrapper">
      <table class="master-table table table-bordered">
        <thead class="table-dark">
          <tr>
            <th class="btn-cell">操作</th>
            <th class="text-nowrap">請求番号</th>
            <th class="text-nowrap">ステータス</th>
            <th class="text-nowrap">請求先顧客</th>
            <th class="text-nowrap">請求日</th>
            <th class="text-nowrap">合計</th>
            <th class="text-nowrap">消費税</th>
            <th class="text-nowrap">繰越金額</th>
            <th class="text-nowrap">総計請求金額</th>
            <th class="text-nowrap">請求書出力日時</th>
            <th class="text-nowrap">作成日時</th>
            <th class="text-nowrap">更新日時</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="monthlyBilling in monthlyBillings" :key="monthlyBilling.id"
            :class="{
              'status-no-sending': monthlyBilling.pdf_output_completed === 'false',
              'status-all-ok': monthlyBilling.pdf_output_completed === 'true'
            }"
          >
            <td  class="d-flex text-nowrap">
              <router-link
                v-if="monthlyBilling"
                :to="{
                  name: 'BillingDetail',
                  params: { id: monthlyBilling.id, year: monthlyBilling.billing_year, month: monthlyBilling.billing_month },
                  query: { ...getSearchQuery(), year: monthlyBilling.billing_year, month: monthlyBilling.billing_month }
                }"
              >
                <button type="button" class="btn btn-sm btn-info mx-1">請求書詳細</button>
              </router-link>

              <router-link
                v-if="monthlyBilling && (monthlyBilling.status === 1 || monthlyBilling.status === 2 || monthlyBilling.status === 3)"
                :to="{
                  name: 'BillingEdit',
                  params: { id: monthlyBilling.id, year: monthlyBilling.billing_year, month: monthlyBilling.billing_month },
                  query: { ...getSearchQuery(), year: monthlyBilling.billing_year, month: monthlyBilling.billing_month, from: 'list' }
                }"
              >
                <button type="button" class="btn btn-sm btn-warning mx-2">編集</button>
              </router-link>

              <button class="btn btn-sm btn-danger" data-bs-toggle="modal" data-bs-target="#deleteModal" @click="handleDeleteClick(monthlyBilling.id)">削除</button>
            </td>
            <td class="text-nowrap">{{ monthlyBilling.parent_billing_no }}</td>
            <td class="text-nowrap">
              <span v-if="!(monthlyBilling)">請求書未発行</span>
              <span v-if="monthlyBilling">
                <span v-if="monthlyBilling.pdf_output_completed == false">PDF未出力</span>
                <span v-if="monthlyBilling.pdf_output_completed == true">PDF出力済み</span>
              </span>
            </td>
            <td class="multiline-text text-start">{{ monthlyBilling?.m_customer?.customer_nm }}</td>
            <td class="text-nowrap text-start">{{ formatYearMonthDay(monthlyBilling.billing_dt) }}</td>
            <td class="text-nowrap text-end">{{ formatCurrency(monthlyBilling.total_amount) }}</td>
            <td class="text-nowrap text-end">{{ formatCurrency(savedBillingAmounts(monthlyBilling).tax_amount) }}（{{ monthlyBilling.tax_rate }}%）</td>
            <td class="text-nowrap text-end">{{ formatCurrency(monthlyBilling.carryover_amount) }}</td>
            <td class="text-nowrap text-end">{{ formatCurrency(monthlyBilling.billing_total_amount) }}</td>
            <td class="text-nowrap text-start">{{ formatFullDateTime(monthlyBilling.pdf_latest_output_datetime) }}</td>
            <td class="text-nowrap text-start">{{ formatFullDateTime(monthlyBilling.created_at) }}</td>
            <td class="text-nowrap text-start">{{ formatFullDateTime(monthlyBilling.updated_at) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 削除確認モーダル -->
    <DeleteConfirmationModal
      :paramsId="paramsIdToDelete"
      @confirmDelete="handleConfirmDelete"
    />

  </div>
</template>

<script>
import { savedBillingAmounts } from '@/utils/billingAmounts';
import { computed, ref, watch, watchEffect } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useRoute } from 'vue-router';
import {
  getMonthlyBillings,
  getSelectBoxCustomers,
  deleteMonthlyBilling
} from '@/api/axios';
import CustomerSelectBox from '@/components/searchSelectBoxes/CustomerSelectBox.vue';
import SearchPanelWrapper from '@/components/searchPanels/SearchPanelWrapper.vue';
import DeleteConfirmationModal from '@/components/shared/DeleteConfirmationModal.vue';
import Pagination from '@/components/shared/Pagination.vue';

export default {
  components: {
    CustomerSelectBox,
    SearchPanelWrapper,
    DeleteConfirmationModal,
    Pagination,
  },
  setup() {
    const title = ref('月別請求建物情報一覧');

    watchEffect(() => {
      document.title = title.value;
    });

    const store = useStore();
    const router = useRouter();
    const route = useRoute();
    const customers = ref([]);
    const monthlyBillings = ref([]);
    const years = ref([]);
    const months = ref([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]);

    // 現在の年月を取得
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();

    // 年のリスト作成（±5年）
    for (let i = currentYear - 5; i <= currentYear + 5; i++) {
      years.value.push(i);
    }

    // 検索パネルの展開状態を管理するためのリアクティブプロパティ
    const isSearchExpanded = ref(true);

    // ▼▼ 共通：検索条件をそのまま query として返す ▼▼
    const getSearchQuery = () => ({
      billing_year_from: searchYearFrom.value,
      billing_month_from: searchMonthFrom.value,
      billing_year_to: searchYearTo.value,
      billing_month_to: searchMonthTo.value,
      billing_billing_no: searchBillingNo.value,
      billing_customer_name: searchCustomerName.value,
      billing_pdf_output_completed: searchPdfOutputFlag.value,
      billing_page: currentPage.value || 1
    });

    // 検索用のデータプロパティ：ここで初期値を現在の年月に設定
    const searchYearFrom = ref(null);
    const searchMonthFrom = ref(null);
    const searchYearTo = ref(null);
    const searchMonthTo = ref(null);
    const searchBillingNo = ref('');
    const searchCustomerName = ref('');
    const searchPdfOutputFlag = ref(''); // PDF出力フラグの検索用

    const ymToNumber = (y, m) => {
      if (!y || !m) return null;
      return Number(y) * 100 + Number(m);
    };

    // From変更時 → To を同じ年月に合わせる
    watch([searchYearFrom, searchMonthFrom], ([newYearFrom, newMonthFrom]) => {
      if (!newYearFrom || !newMonthFrom) return;
      searchYearTo.value = newYearFrom;
      searchMonthTo.value = newMonthFrom;
    });

    // Toの選択肢はFrom以降に制限
    const filteredYearsTo = computed(() => {
      if (!searchYearFrom.value) return years.value;
      return years.value.filter((y) => Number(y) >= Number(searchYearFrom.value));
    });

    const filteredMonthsTo = computed(() => {
      if (!searchYearFrom.value || !searchMonthFrom.value) return months.value;
      if (searchYearTo.value > searchYearFrom.value) return months.value;
      if (searchYearTo.value === searchYearFrom.value) {
        return months.value.filter((m) => m >= Number(searchMonthFrom.value));
      }
      return months.value;
    });

    // From > To の場合はToをFromへ補正
    watch([searchYearFrom, searchMonthFrom, searchYearTo, searchMonthTo], () => {
      const from = ymToNumber(searchYearFrom.value, searchMonthFrom.value);
      const to = ymToNumber(searchYearTo.value, searchMonthTo.value);
      if (from && to && from > to) {
        searchYearTo.value = searchYearFrom.value;
        searchMonthTo.value = searchMonthFrom.value;
      }
    });

    // テーブルの高さの動的な設定
    const topFixArea = ref(null);
    const tableWrapper = ref(null);
    const paginationContainer = ref(null);

    // ページネーション
    const currentPage = ref(1);
    const totalPages = ref(50); // トータルページ数を管理するためのプロパティ

    const fetchBuildings = async (page = 1) => {
      try {
        const response = await getMonthlyBillings({
          year_from: searchYearFrom.value,
          month_from: searchMonthFrom.value,
          year_to: searchYearTo.value,
          month_to: searchMonthTo.value,
          billing_no: searchBillingNo.value,
          customer_name: searchCustomerName.value,
          pdf_output_completed: searchPdfOutputFlag.value,
          page: page
        });
        monthlyBillings.value = response.data.monthly_billings;
        totalPages.value = response.data.meta.total_pages;
        currentPage.value = response.data.meta.current_page;
      } catch (error) {
        console.error('請求情報の取得に失敗しました:', error);
      }

      // 顧客情報（検索用）
      try {
        const response = await getSelectBoxCustomers();
        customers.value = response.data;
      } catch (error) {
        console.error('顧客情報ーデータの取得に失敗しました:', error);
      }
    };

    const handleChangePage = (page) => {
      if (page >= 1 && page <= totalPages.value) {
        fetchBuildings(page);

        const q = getSearchQuery();
        q.billing_page = page;

        router.replace({
          path: '/monthly-billings',
          query: q
        });

        // スクロールを tableWrapper のトップへ
        if (tableWrapper.value) {
          tableWrapper.value.scrollTop = 0;
        }
      }
    };

    // 削除モーダルへ渡すID
    const paramsIdToDelete = ref(null);

    // 削除くリック時のIDの受け渡し
    const handleDeleteClick = (monthlyBillingId) => {
      paramsIdToDelete.value = monthlyBillingId;
    };

    // 削除処理
    const handleConfirmDelete = async (monthlyBillingId) => {
      try {
        await deleteMonthlyBilling(monthlyBillingId);
        // リストから臨時作業情報を削除
        monthlyBillings.value = monthlyBillings.value.filter(monthlyBilling => monthlyBilling.id !== monthlyBillingId);
        store.dispatch('setFlashMessage', { message: '請求書情報が削除されました', type: 'success' });
        // クエリをルートに反映
        router.push({
          path: '/monthly-billings',
          query: getSearchQuery()
        });
      } catch (error) {
        // サーバーからのエラーメッセージを取得
        const errorMessage = error.response?.data?.message || '請求書情報削除に失敗しました';
        store.dispatch('setFlashMessage', { message: errorMessage, type: 'danger' });
      }
    };

    // 請求のある建物を検索するメソッド
    const searchBuildings = async () => {
      await fetchBuildings(1);

      const q = getSearchQuery();
      q.billing_page = 1;

      // クエリに反映（null は送らないように）
      router.push({
        path: '/monthly-billings',
        query: q
      });
    };

    // 請求のある建物を検索リセットするメソッド
    const searchResetBuildings = async () => {
      // 検索条件を現在に戻す
      searchYearFrom.value = '';
      searchMonthFrom.value = '';
      searchYearTo.value = '';
      searchMonthTo.value = '';
      searchBillingNo.value = '';
      searchCustomerName.value = '';
      searchPdfOutputFlag.value = '';

      await fetchBuildings(1);

      const q = getSearchQuery();
      q.billing_page = 1;

      // クエリもリセット
      router.replace({
        path: '/monthly-billings',
        query: q
      });
    };

    // クエリパラメータの監視（画面遷移後の自動反映）
    watch(
      () => route.query,
      (newQuery) => {
        // クエリから各検索条件を取り出し、ref に代入
        searchYearFrom.value = newQuery.billing_year_from !== undefined ? parseInt(newQuery.billing_year_from) || null : null;
        searchMonthFrom.value = newQuery.billing_month_from !== undefined ? parseInt(newQuery.billing_month_from) || null : null;
        searchYearTo.value = newQuery.billing_year_to !== undefined ? parseInt(newQuery.billing_year_to) || null : null;
        searchMonthTo.value = newQuery.billing_month_to !== undefined ? parseInt(newQuery.billing_month_to) || null : null;

        searchBillingNo.value = newQuery.billing_billing_no || '';
        searchCustomerName.value = newQuery.billing_customer_name || '';
        searchPdfOutputFlag.value = newQuery.billing_pdf_output_completed || '';
        currentPage.value = newQuery.billing_page ? Number(newQuery.billing_page) : 1;

        // 初回またはクエリ変更時にデータを取得
        fetchBuildings(currentPage.value);
      },
      { immediate: true }
    );

    // 年月日
    const formatYearMonthDay = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
    };

    // 年月日時分秒
    const formatFullDateTime = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);

      const year = date.getFullYear();
      const month = date.getMonth() + 1; // 月は0始まり
      const day = date.getDate();
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const seconds = date.getSeconds();

      return `${year}年${month}月${day}日 ${hours}時${minutes}分${seconds}秒`;
    };

    // Format currency
    const formatCurrency = (value) => {
      if (value) {
        return new Intl.NumberFormat('ja-JP', {
          style: 'currency',
          currency: 'JPY',
        }).format(value);
      }
      return '¥0';
    };

    // 初回データ取得は query 監視で実行

    return {
      title,
      route,
      customers,
      monthlyBillings,
      years,
      months,
      paramsIdToDelete,
      handleDeleteClick,
      handleConfirmDelete,
      fetchBuildings,
      searchYearFrom,
      searchMonthFrom,
      searchYearTo,
      searchMonthTo,
      filteredYearsTo,
      filteredMonthsTo,
      searchBillingNo,
      searchCustomerName,
      searchPdfOutputFlag,
      getSearchQuery,
      formatYearMonthDay,
      formatFullDateTime,
      searchBuildings,
      searchResetBuildings,
      formatCurrency,
      savedBillingAmounts,
      handleChangePage,
      currentPage,
      totalPages,
      topFixArea,
      tableWrapper,
      paginationContainer,
      isSearchExpanded
    };
  }
};
</script>

<style scoped>
.building-item {
  border: 1px solid #ddd;
  padding: 10px;
  margin-bottom: 10px;
  border-radius: 5px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.building-item:hover {
  background-color: #f5f5f5;
}

.table-wrapper {
  background: #fff;
  border: 1px solid #BBBBBB;
  width: 100%;
  overflow-x: scroll;
}

.table-wrapper table{
  margin-bottom: 0;
}

.table-wrapper thead th {
  background: #003366 !important; /* 背景色を設定し、スクロール中に背景が透けるのを防ぐ */
  color: #fff !important;
}

table th,
table td{
  font-size: 14px;
}

.table-wrapper .sticky-left,
.table-wrapper .sticky-left-second,
.table-wrapper .sticky-left-third
{
  position: sticky;
  z-index: 100
}
.table-wrapper .sticky-left:before,
.table-wrapper .sticky-left:after,
.table-wrapper .sticky-left-second:before,
.table-wrapper .sticky-left-second:after,
.table-wrapper .sticky-left-third:before,
.table-wrapper .sticky-left-third:after
{
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
  border-left: 1px solid #DEE2E6;
  z-index: 99;
}
.table-wrapper .sticky-left:before,
.table-wrapper .sticky-left:after,
.table-wrapper .sticky-left-third:before,
.table-wrapper .sticky-left-third:after{ content: ""; }
.table-wrapper .sticky-left{
  left: 1px;
  min-width: 200px;
  width: 200px;
  max-width: 200px;
}
.table-wrapper .sticky-left:before{ left: 0; }
.table-wrapper .sticky-left:after{ left: 199px; }

.table-wrapper .sticky-left-second{
  left: 201px;
  min-width: 224px;
  width: 224px;
  max-width: 224px;
}
.table-wrapper .sticky-left-second:before{ left: -1px; }
.table-wrapper .sticky-left-second:after{ left: 223px; }

.table-wrapper .sticky-left-third{
  left: 425px;
  min-width: 120px;
  width: 120px;
  max-width: 120px;
}
.table-wrapper .sticky-left-third:before{ left: -1px; }
.table-wrapper .sticky-left-third:after{ left: 119px; }

.status-no-billing td {
  background-color: #FFD5EC !important;
  color: #ff0000;
}

.status-no-sending td {
  background-color: #FFDBC9 !important;
  color: #000;
}

.status-no-schedule td {
  background-color: #CCFFCC !important;
  color: #000;
}

.status-no-confirm td {
  background-color: #FFFFCC !important;
  color: #000;
}

.status-all-ok td {
  background-color: #E6FFE9 !important;
  color: #000;
}
</style>
